import React, { useState, useEffect } from "react";
import { liveUrl, token } from "./url";
import Cookies from "js-cookie";
import LoginComponent from "./LoginComponent";

export default function Report({ isLoggedIn: initialLoginState = false }) {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(initialLoginState);
  const [phone, setPhone] = useState("");

  // ✅ On mount, load phone from cookies
  useEffect(() => {
    const savedPhone = Cookies.get("phone");
    if (savedPhone) setPhone(savedPhone);
  }, []);

  // ✅ Called when user logs in successfully
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    const savedPhone = Cookies.get("phone");
    if (savedPhone) setPhone(savedPhone);
  };

  // ✅ Report/Comment submission
  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setError("Please enter a comment.");
      setSuccess("");
      return;
    }

    // Check if phone is available (from login or cookie)
    const phoneFromCookie = Cookies.get("phone") || phone;
    if (!phoneFromCookie) {
      setError("Phone number missing. Please log in again.");
      setSuccess("");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    // Extract property ID from current URL
    const pathParts = window.location.pathname.split("/").pop().split("-");
    const propertyId = pathParts[1] || "unknown";

    // ✅ Prepare payload
    const payload = {
      username: Cookies.get("userName") || "anonymous",
      phone: phoneFromCookie,
      property_id: propertyId,
      comment: comment.trim(),
    };

    try {
      const response = await fetch(`${liveUrl}api/report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.status) {
        const newComment = {
          username: payload.username,
          comment: payload.comment,
          created_at: new Date().toLocaleString(),
          id: result.insert_id || Date.now(),
        };
        setComments([newComment, ...comments]);
        setComment("");
        setError("");
        setSuccess("Report submitted successfully!");
      } else {
        throw new Error(result.message || "Failed to submit report.");
      }
    } catch (err) {
      setError(
        err.message.includes("CORS")
          ? "CORS error: Backend must allow requests from frontend domain."
          : `Failed to submit report: ${err.message}`
      );
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Logout handler
  const handleLogout = () => {
    setIsLoggedIn(false);
    Cookies.remove("token");
    Cookies.remove("userName");
    Cookies.remove("phone");
    Cookies.remove("isLoggedIn");
    Cookies.remove("userId");
    setPhone("");
    setComment("");
    setError("");
    setSuccess("Logged out successfully.");
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white border border-gray-200 shadow-lg rounded-lg p-4">
      {!isLoggedIn ? (
        <LoginComponent onLoginSuccess={handleLoginSuccess} />
      ) : (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Submit a Report
            </h3>
            <button
              onClick={handleLogout}
              className="text-sm text-red-500 hover:underline"
            >
              Logout
            </button>
          </div>

          <textarea
            placeholder="Write your comment..."
            className="w-full p-3 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="4"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={loading}
          ></textarea>

          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
          {success && <p className="text-green-600 text-sm mb-2">{success}</p>}

          <button
            onClick={handleComment}
            className={`bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Report"}
          </button>
        </div>
      )}
    </div>
  );
}
